import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, tap } from 'rxjs';
import { environment } from '../environments/environment';
import type {
  CaseResponse as CaseData,
  TimelineStep,
  CreateCase as CreateCaseRequest,
  UploadDocumentsResponse,
  UploadedDocument,
  AuthorizationResponse,
  Document as CaseDocument,
  GetDocumentsResponse,
  ConditionDefinition,
  ConditionItem as ConditionItemPayload,
  ConditionsResponse,
  Comment as CaseComment,
  GetCommentsResponse,
  NoticeOfAppealDocument,
  NoticeOfAppealResponse,
} from '@kin/property-tax-types';

export type {
  CaseData, TimelineStep, CreateCaseRequest, UploadDocumentsResponse,
  UploadedDocument, AuthorizationResponse, CaseDocument, GetDocumentsResponse,
  ConditionDefinition, ConditionItemPayload,
  ConditionsResponse, CaseComment, GetCommentsResponse,
  NoticeOfAppealDocument, NoticeOfAppealResponse,
};

@Injectable({ providedIn: 'root' })
export class CaseService {
  private http = inject(HttpClient);

  private _case: CaseData | null = null;
  private _authorization: AuthorizationResponse | null = null;

  get caseData(): CaseData | null {
    return this._case;
  }

  clear(): void {
    this._case = null;
  }

  createCase(request: CreateCaseRequest): Observable<CaseData> {
    return this.http.post<CaseData>(`${environment.apiUrl}/case`, request).pipe(
      tap(c => (this._case = c)),
    );
  }

  getCase(): Observable<CaseData | null> {
    if (this._case) return of(this._case);
    return this.http.get<CaseData>(`${environment.apiUrl}/case`).pipe(
      tap(c => (this._case = c)),
    );
  }

  getDocuments(): Observable<GetDocumentsResponse> {
    return this.http.get<GetDocumentsResponse>(`${environment.apiUrl}/case/documents`);
  }

  uploadDocuments(files: File[]): Observable<UploadDocumentsResponse> {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file, file.name);
    }
    return this.http.post<UploadDocumentsResponse>(`${environment.apiUrl}/case/documents`, formData);
  }

  getConditionDocuments(conditionId: string): Observable<GetDocumentsResponse> {
    return this.http.get<GetDocumentsResponse>(`${environment.apiUrl}/case/conditions/${conditionId}/documents`);
  }

  uploadConditionDocuments(conditionId: string, files: File[]): Observable<UploadDocumentsResponse> {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file, file.name);
    }
    return this.http.post<UploadDocumentsResponse>(`${environment.apiUrl}/case/conditions/${conditionId}/documents`, formData);
  }

  deleteDocument(id: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/case/documents/${id}`);
  }

  getComments(): Observable<GetCommentsResponse> {
    return this.http.get<GetCommentsResponse>(`${environment.apiUrl}/case/comments`);
  }

  addComment(body: string): Observable<CaseComment> {
    return this.http.post<CaseComment>(`${environment.apiUrl}/case/comments`, { body });
  }

  deleteComment(id: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/case/comments/${id}`);
  }

  saveAuthorization(): Observable<AuthorizationResponse> {
    return this.http.post<AuthorizationResponse>(`${environment.apiUrl}/case/authorization`, {}).pipe(
      tap(a => (this._authorization = a)),
    );
  }

  getAuthorization(): Observable<AuthorizationResponse> {
    if (this._authorization) return of(this._authorization);
    return this.http.get<AuthorizationResponse>(`${environment.apiUrl}/case/authorization`).pipe(
      tap(a => (this._authorization = a)),
    );
  }

  getNoticeOfAppeal(): Observable<NoticeOfAppealResponse> {
    return this.http.get<NoticeOfAppealResponse>(`${environment.apiUrl}/case/notice-of-appeal`);
  }

  uploadNoticeOfAppeal(file: File): Observable<NoticeOfAppealResponse> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<NoticeOfAppealResponse>(`${environment.apiUrl}/case/notice-of-appeal`, formData);
  }

  deleteNoticeOfAppeal(): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/case/notice-of-appeal`);
  }

  getConditionDefinitions(): Observable<ConditionDefinition[]> {
    return this.http.get<ConditionDefinition[]>(`${environment.apiUrl}/case/condition-definitions`);
  }

  getConditions(): Observable<ConditionsResponse> {
    return this.http.get<ConditionsResponse>(`${environment.apiUrl}/case/conditions`);
  }

  updateConditions(conditions: ConditionItemPayload[]): Observable<ConditionsResponse> {
    return this.http.put<ConditionsResponse>(`${environment.apiUrl}/case/conditions`, { conditions });
  }
}
